export type TeamStaff = { id: string; name: string; role: string; version: number };
export type TeamContact = {
  id: string;
  name: string;
  owner: string;
  stage: string;
  version: number;
  linkedCounts: Record<string, number>;
};
export type TeamTask = {
  id: string;
  title: string;
  owner: string;
  version: number;
  dueAt: string;
  completed: boolean;
  contactId?: string;
  eligibleStaffIds: string[];
  assignmentBlockedReason?: string;
};
export type TeamOverview = {
  staff: TeamStaff[];
  contacts: TeamContact[];
  tasks: TeamTask[];
  workload: {
    staffId: string;
    name: string;
    contacts: number;
    openLeads: number;
    openDeals: number;
    openTasks: number;
    overdueTasks: number;
    upcomingVisits: number;
  }[];
  capabilities: { canManageStaff: boolean; canAssignWork: boolean };
};
export type TeamAssignment =
  { entity: 'contacts'; record: TeamContact } | { entity: 'tasks'; record: TeamTask };
export type TeamAssignmentPreview = {
  previewToken: string;
  entity: 'contacts' | 'tasks';
  entityId: string;
  staffId: string;
  staffVersion: number;
  version: number;
  fromOwner: string;
  toOwner: string;
  scope: string;
  affected: { entity: string; id: string; label: string; version: number; owner: string }[];
  counts: Record<string, number>;
  warnings: string[];
};

export const canManageTeam = (role?: string) => role === 'Super Admin' || role === 'Sales Head';
export const teamHasActiveOwner = (owner: string | undefined, staff: TeamStaff[]) =>
  Boolean(owner?.trim() && staff.some((person) => person.name === owner));

export function teamEligibleStaff(selection: TeamAssignment, staff: TeamStaff[]) {
  if (selection.entity === 'contacts') return staff;
  if (selection.record.completed) return [];
  return staff.filter((person) => selection.record.eligibleStaffIds.includes(person.id));
}

export function teamReplacementStaff(selection: TeamAssignment, staff: TeamStaff[]) {
  return teamEligibleStaff(selection, staff).filter(
    (person) => person.name !== selection.record.owner,
  );
}

export function teamAssignmentPayload(selection: TeamAssignment, staff: TeamStaff) {
  return {
    entity: selection.entity,
    entityId: selection.record.id,
    version: selection.record.version,
    staffId: staff.id,
    staffVersion: staff.version,
  };
}

export function teamContactOptions(contacts: TeamContact[], staff?: TeamStaff) {
  if (!staff) return [];
  return canManageTeam(staff.role)
    ? contacts
    : contacts.filter((contact) => contact.owner === staff.name);
}

export const teamEntityLabel = (entity: string) =>
  ({
    contacts: 'Relationship',
    accounts: 'Company account',
    leads: 'Enquiry',
    opportunities: 'Deal',
    offers: 'Offer',
    tasks: 'Task',
    visits: 'Site visit',
    episodes: 'Acquisition enquiry',
    acquisitionEpisodes: 'Acquisition enquiry',
  })[entity] || entity;

export function teamError(error: unknown) {
  const value = error as { message?: string; details?: { fieldErrors?: Record<string, string[]> } };
  const fields = value.details?.fieldErrors;
  return (
    (fields &&
      Object.entries(fields)
        .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
        .join(' ')) ||
    value.message ||
    'The team workspace could not be updated.'
  );
}
